import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../constants';
import { useAppDispatch, useFeatureFlag } from '../../../hooks';
import { setOrgId } from '../../../redux/reducers';
import { LD_FeatureFlags } from '../../../types';
import { conditionalArray } from '../../../utils';
import {
  Badge,
  ButtonGroup,
  DesignerModalCard,
  Icon,
  Icons,
  OrgSelector,
  TextButton,
  Typography,
  TypographyType,
} from '../../shared';
import { CreateSimulationEnum, CreateSimulationStepEnum } from './CreateSimulationModal';

const INTEGRATION_BADGE_SIZE = 42;

interface CreateSimulationTypeStepProps {
  isSuperAdmin: boolean;
  selectedCreateSimulationType: CreateSimulationEnum;
  setSelectedCreateSimulationType: Dispatch<SetStateAction<CreateSimulationEnum>>;
  setTargetOrgId: Dispatch<SetStateAction<string | undefined>>;
  setCurrentStep: Dispatch<SetStateAction<CreateSimulationStepEnum>>;
  onCancel: () => void;
  targetOrgId?: string;
}

const CreateSimulationTypeStep = ({
  isSuperAdmin,
  selectedCreateSimulationType,
  setSelectedCreateSimulationType,
  setTargetOrgId,
  setCurrentStep,
  onCancel,
  targetOrgId,
}: CreateSimulationTypeStepProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const salesloftFF = useFeatureFlag(LD_FeatureFlags.RELEASE_SALESLOFT_INTEGRATION);

  const createSimulationTypeOptions = useMemo(
    () => [
      {
        id: CreateSimulationEnum.CUSTOM,
        isSelected: selectedCreateSimulationType === CreateSimulationEnum.CUSTOM,
        title: 'Create new simulation',
        onSelect: () => setSelectedCreateSimulationType(CreateSimulationEnum.CUSTOM),
      },
      {
        id: CreateSimulationEnum.TEMPLATE,
        isSelected: selectedCreateSimulationType === CreateSimulationEnum.TEMPLATE,
        title: 'Start from existing simulation',
        onSelect: () => setSelectedCreateSimulationType(CreateSimulationEnum.TEMPLATE),
      },
      {
        id: CreateSimulationEnum.AI_TWIN,
        isSelected: selectedCreateSimulationType === CreateSimulationEnum.AI_TWIN,
        title: (
          <div className="flex items-center justify-between">
            <Typography type={TypographyType.H4}>Create AI twin</Typography>
            <div className="flex gap-1">
              <Badge
                className="bg-gong-purple-light"
                startIcon={<Icons icon={Icon.GONG} color="text-transparent" size={INTEGRATION_BADGE_SIZE} />}
              />
              {!!salesloftFF && (
                <Badge
                  className="bg-salesloft-teal-light"
                  startIcon={<Icons icon={Icon.SALESLOFT} color="text-transparent" size={INTEGRATION_BADGE_SIZE} />}
                />
              )}
              <Badge label="Import" startIcon={<Icons icon={Icon.UPLOAD} size={12} />} />
            </div>
          </div>
        ),
        onSelect: () => setSelectedCreateSimulationType(CreateSimulationEnum.AI_TWIN),
      },
      // Only show prompt template option if user is super admin.
      ...conditionalArray(isSuperAdmin, {
        id: CreateSimulationEnum.PROMPT_TEMPLATE,
        isSelected: selectedCreateSimulationType === CreateSimulationEnum.PROMPT_TEMPLATE,
        title: 'Create prompt template',
        onSelect: () => setSelectedCreateSimulationType(CreateSimulationEnum.PROMPT_TEMPLATE),
      }),
    ],
    [selectedCreateSimulationType, isSuperAdmin]
  );

  const onNext = useCallback(() => {
    // If selected is create prompt template, navigate to the prompt templates page.
    if (selectedCreateSimulationType === CreateSimulationEnum.PROMPT_TEMPLATE) {
      navigate(AppRoutes.PROMPT_TEMPLATES);
      return;
    }

    if (!targetOrgId) return;

    // Moves to create simulation step according to the selected simulation type.
    setCurrentStep(CreateSimulationStepEnum.CREATE_SIMULATION_STEP);
    dispatch(setOrgId(targetOrgId));
  }, [targetOrgId, dispatch, navigate, selectedCreateSimulationType]);

  const showOrgSelector =
    isSuperAdmin &&
    (selectedCreateSimulationType === CreateSimulationEnum.CUSTOM ||
      selectedCreateSimulationType === CreateSimulationEnum.TEMPLATE);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        {createSimulationTypeOptions.map((option) => (
          <DesignerModalCard key={option.id} {...option} />
        ))}
      </div>
      {showOrgSelector && (
        <div className="flex flex-col gap-4">
          <Typography type={TypographyType.BASE}>What organization is this simulation for?</Typography>
          <OrgSelector selectFirstAsDefault selectedOrgId={targetOrgId} onChange={setTargetOrgId} />
        </div>
      )}
      <ButtonGroup>
        <TextButton onClick={onNext} text="Next" disabled={!targetOrgId && showOrgSelector} />
        <TextButton onClick={onCancel} text="Cancel" />
      </ButtonGroup>
    </div>
  );
};

export default CreateSimulationTypeStep;
